import { formatarDinheiro } from '@/nucleo/dinheiro';

import type { PontoSerieMensal, RegimeIndicadores } from './tipos';

/** Alíquota patronal de INSS sobre o bruto PF (regra TEG-SP). */
export const ALIQUOTA_INSS_PF = 0.04;

export const inssPf = (brutoPf: number): number => brutoPf * ALIQUOTA_INSS_PF;

export const brutoComInssPf = (brutoPf: number): number => brutoPf + inssPf(brutoPf);

/** Totais derivados de um regime, com INSS calculado só quando `aplicaInss` (PF). */
export interface TotaisRegime {
  bruto: number;
  criancasNc: number;
  criancasCad: number;
  criancasTotal: number;
  kmAdic: number;
  kmTotal: number;
  veiculosConv: number;
  veiculosAcess: number;
  veiculosCreche: number;
  veiculosTotal: number;
  condutores: number;
  inss: number;
  brutoComInss: number;
}

export function totaisRegime(r: RegimeIndicadores, aplicaInss: boolean): TotaisRegime {
  const inss = aplicaInss ? inssPf(r.bruto) : 0;
  return {
    bruto: r.bruto,
    criancasNc: r.criancasNc,
    criancasCad: r.criancasCad,
    criancasTotal: r.criancasNc + r.criancasCad,
    kmAdic: r.kmAdic,
    kmTotal: r.kmTotal,
    veiculosConv: r.veiculosConv,
    veiculosAcess: r.veiculosAcess,
    veiculosCreche: r.veiculosCreche,
    veiculosTotal: r.veiculosConv + r.veiculosAcess + r.veiculosCreche,
    condutores: r.condutores,
    inss,
    brutoComInss: r.bruto + inss,
  };
}

/** Variação mês a mês (último vs. penúltimo ponto). `null` sem dado suficiente. */
export function variacaoMensal(
  serie: PontoSerieMensal[],
  campo: keyof PontoSerieMensal,
): number | null {
  if (serie.length < 2) return null;
  const atual = Number(serie[serie.length - 1]?.[campo] ?? 0);
  const anterior = Number(serie[serie.length - 2]?.[campo] ?? 0);
  if (!anterior) return null;
  return (atual - anterior) / anterior;
}

/** Quantil por interpolação linear — usado para mediana e P10/P90 do Ato 3. */
export function quantil(valores: number[], q: number): number {
  if (valores.length === 0) return 0;
  const ordenado = [...valores].sort((a, b) => a - b);
  const posicao = (ordenado.length - 1) * q;
  const base = Math.floor(posicao);
  const resto = posicao - base;
  const atual = ordenado[base];
  if (atual === undefined) return 0;
  const proximo = ordenado[base + 1];
  return proximo !== undefined ? atual + resto * (proximo - atual) : atual;
}

export const fmtInt = (n: number): string => new Intl.NumberFormat('pt-BR').format(Math.round(n));

export const fmtDec = (n: number, casas = 2): string =>
  new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  }).format(n);

export const fmtKmCurto = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)} mi km`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(0)} mil km`
      : `${fmtInt(n)} km`;

export const fmtDinheiroCurto = (n: number): string =>
  n >= 1_000_000
    ? `R$ ${(n / 1_000_000).toFixed(1)} mi`
    : n >= 1_000
      ? `R$ ${(n / 1_000).toFixed(0)} mil`
      : formatarDinheiro(n);

const NOMES_MES = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];
const NOMES_MES_CURTO = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

function partesDoMes(mesIso: string): { ano: number; mesIndice: number } {
  const [anoStr, mesStr] = mesIso.split('-');
  return { ano: Number(anoStr), mesIndice: Math.max(0, Math.min(11, Number(mesStr) - 1)) };
}

/** Competência "AAAA-MM" por extenso, como "julho de 2026". */
export function formatarMesLongo(mesIso: string): string {
  const { ano, mesIndice } = partesDoMes(mesIso);
  return `${NOMES_MES[mesIndice]} de ${ano}`;
}

/** Competência "AAAA-MM" abreviada, como "jul/26". */
export function formatarMesCurto(mesIso: string): string {
  const { ano, mesIndice } = partesDoMes(mesIso);
  return `${NOMES_MES_CURTO[mesIndice]}/${String(ano).slice(-2)}`;
}
